import React, { useState, useRef, useEffect, useMemo } from "react";
import { FaChevronDown } from "react-icons/fa";
import { X } from "lucide-react";

const Select = ({
  label,
  value,
  onChange,
  options = [],
  placeholder = "Select option",
  error = "",
  disabled = false,
  loading = false,
  clearable = false,
  className = "",
  required = false,
  variant = "default", // ✅ NEW PROP
}) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [options]);

  const selectedOption = useMemo(() => {
    return options.find((opt) => String(opt.value) === String(value)) || null;
  }, [value, options]);

  const handleSelect = (val) => {
    if (disabled) return;
    onChange?.(val);
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange?.("");
  };

  return (
    <div
      className={`w-full flex flex-col gap-1 relative ${className}`}
      ref={dropdownRef}
    >
      {label && (
        <label className="text-sm font-medium text-gray-700 flex items-center">
          {label}
          {required && !disabled && (
            <span className="text-red-500 ml-1">*</span>
          )}
        </label>
      )}

      {/* Trigger */}
      <div
        tabIndex={0}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === "Enter") setOpen((prev) => !prev);
        }}
        className={`
          relative w-full h-11 px-3 border rounded-lg bg-white
          flex items-center justify-between
          transition-all
          ${
            disabled
              ? "opacity-60 cursor-not-allowed bg-gray-100 text-gray-400"
              : "cursor-pointer"
          }
          ${
            error
              ? "border-red-500"
              : "border-gray-300 hover:border-gray-400 focus:ring-2 focus:ring-[#FF6943]"
          }
        `}
      >
        {/* Selected */}
        {selectedOption ? (
          <div className="flex items-center gap-2 truncate">
            {variant === "withImages" && selectedOption.image && (
              <img
                src={selectedOption.image}
                alt={selectedOption.label}
                className="w-5 h-5 object-cover rounded-full"
              />
            )}
            <span className="text-sm font-medium truncate">
              {selectedOption.label}
            </span>
          </div>
        ) : (
          <span className="text-sm text-gray-400">
            {loading ? "Loading..." : placeholder}
          </span>
        )}

        <div className="flex items-center gap-2">
          {clearable && selectedOption && (
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-400 hover:text-red-500"
            >
              <X size={14} />
            </button>
          )}

          <FaChevronDown
            size={10}
            className={`transition-transform ${open ? "rotate-180" : ""}`}
          />
        </div>
      </div>

      {/* Dropdown */}
      {open && !disabled && (
        <ul
          className="
            absolute z-50 w-full mt-1
            bg-white border border-gray-200
            rounded-lg shadow-lg
            max-h-60 overflow-y-auto
          "
          style={{ top: "100%" }}
        >
          {loading ? (
            <li className="px-3 py-2 text-sm text-gray-400">Loading...</li>
          ) : options.length === 0 ? (
            <li className="px-3 py-2 text-sm text-gray-400">
              No options found
            </li>
          ) : (
            options.map((opt) => {
              const isSelected = String(opt.value) === String(value);

              return (
                <li
                  key={opt.value}
                  onClick={() => handleSelect(opt.value)}
                  className={`
                    px-3 py-2 cursor-pointer text-sm flex items-center gap-2
                    ${
                      isSelected
                        ? "bg-orange-50 text-[#FF6943] font-medium"
                        : "hover:bg-gray-100"
                    }
                  `}
                >
                  {variant === "withImages" && opt.image && (
                    <img
                      src={opt.image}
                      alt={opt.label}
                      className="w-5 h-5 object-cover rounded-full"
                    />
                  )}
                  {opt.label}
                </li>
              );
            })
          )}
        </ul>
      )}

      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}

      {required && (
        <select
          value={value || ""}
          onChange={() => {}}
          required
          className="hidden"
          tabIndex={-1}
        >
          <option value="" disabled hidden></option>
        </select>
      )}
    </div>
  );
};

export default Select;
